import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Table, TableColumnConfig, TableRowActions } from '@frontend/design-system';
import { useDataSourceColumn } from '../../hooks/use-data-source-column';
import { ProviderNameCell, ShowOnCalendarCell } from './ManagePractitionersV3Cells';
import { ManagePractitionersV3CommonTableProps } from './types';

type ManagePractitionersV3LargeTableProps = ManagePractitionersV3CommonTableProps & {
  tableRowsAction: TableRowActions<Provider>;
};

export const ManagePractitionersV3LargeTable = ({
  locationId,
  pageConfig,
  setPageConfig,
  tableRowsAction,
  isIntegratedOffice,
  debouncedSearchValue,
}: ManagePractitionersV3LargeTableProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { invalidateListProviders } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const { data: practitionerData, isLoading } = SchedulerV3.Helpers.useGetProvidersV3Data({
    selectedLocationId: locationId || '',
    debouncedSearchValue,
    pageConfig,
    selectedLocationIds: locationId ? [locationId] : [],
  });
  const providerList = practitionerData?.providers || [];
  const totalCount = practitionerData?.totalCount || 0;

  const noExternalNamesExist = isIntegratedOffice && providerList.every((provider) => !provider.externalName);
  const hideExternalNameColumn = !isIntegratedOffice || noExternalNamesExist;

  const {
    shouldShowColumn,
    cellRenderer: dataSourceCellRenderer,
    accessor: dataSourceAccessor,
  } = useDataSourceColumn<Provider>({
    sourceIdField: 'sourceId',
  });

  const handleTableRefetch = () => {
    invalidateListProviders(locationId);
  };

  const tableColumns = useMemo(
    (): TableColumnConfig<Provider>[] => [
      {
        Header: t('Display Name'),
        id: 'displayName',
        accessor: (row) => row,
        cellRenderer: (row) => <ProviderNameCell provider={row} />,
        sortType: (a, b) => {
          const nameA = a.original.displayName || '';
          const nameB = b.original.displayName || '';
          return nameA.localeCompare(nameB);
        },
        minWidth: 120,
        maxWidth: 420,
      },
      {
        Header: t('External Name'),
        id: 'externalName',
        accessor: (row) => row,
        cellRenderer: (row) => row.externalName || '-',
        sortType: (a, b) => {
          const nameA = a.original.externalName || '';
          const nameB = b.original.externalName || '';
          return nameA.localeCompare(nameB);
        },
        minWidth: 120,
        maxWidth: 420,
        omit: hideExternalNameColumn, // External name is only relevant for integrated offices
      },
      {
        Header: t('First Name'),
        id: 'firstName',
        accessor: (row) => row,
        cellRenderer: (row) => row.firstName || '-',
        sortType: (a, b) => {
          const nameA = a.original.firstName || '';
          const nameB = b.original.firstName || '';
          return nameA.localeCompare(nameB);
        },
        minWidth: 120,
        maxWidth: !isIntegratedOffice ? 320 : 210,
      },
      {
        Header: t('Last Name'),
        id: 'lastName',
        accessor: (row) => row,
        cellRenderer: (row) => row.lastName || '-',
        sortType: (a, b) => {
          const nameA = a.original.lastName || '';
          const nameB = b.original.lastName || '';
          return nameA.localeCompare(nameB);
        },
        minWidth: 120,
        maxWidth: !isIntegratedOffice ? 320 : 210,
      },
      {
        Header: t('Source'),
        id: 'source',
        omit: !shouldShowColumn,
        accessor: dataSourceAccessor,
        cellRenderer: dataSourceCellRenderer,
        minWidth: 120,
        maxWidth: 180,
      },
      {
        Header: t('Show on Calendar'),
        id: 'showOnCalendar',
        accessor: (row) => row,
        cellRenderer: (cellValue) => <ShowOnCalendarCell provider={cellValue} />,
        sortType: (a, b) => Number(a.original.hidden || 0) - Number(b.original.hidden || 0),
        minWidth: 150,
        maxWidth: 180,
      },
    ],
    [dataSourceAccessor, dataSourceCellRenderer, hideExternalNameColumn, isIntegratedOffice, shouldShowColumn, t]
  );

  return (
    <>
      <Table
        isLoading={isLoading}
        colConfig={tableColumns}
        rowActions={tableRowsAction}
        tableStyle={{
          name: 'manage-practitioner-v3-table',
          styles: `
            margin-bottom: ${theme.spacing(4)};
            .table__cell {
              justify-content: center;  // align unarchive icon in center of the cell
            }`,
        }}
        data={providerList || []}
        isPaginated
        hasResponsiveColWidths
        hasGlobalSearch
        globalSearchConfig={{
          initialValue: '',
          placeholder: t('Search Provider Name'),
          searchHandler: (searchValue) => {
            const trimmedSearchValue = searchValue.trim();
            // search data if there is more than one character in the search field since backend doesn't support single character search
            const hasOneSearchChar = trimmedSearchValue.length === 1;
            if (totalCount && !hasOneSearchChar) {
              setPageConfig((prev) => ({ ...prev, page: 1, searchValue: trimmedSearchValue }));
            } else if (trimmedSearchValue.length === 0) {
              setPageConfig((prev) => ({ ...prev, searchValue: trimmedSearchValue }));
            }
          },
        }}
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: t('Refetch'),
            onClick: handleTableRefetch,
            type: 'icon',
          },
        ]}
        manualPaginationConfig={{
          showNumRowSelector: true,
          showGoToPageField: true,
          page: pageConfig.page || 1,
          rowsPerPageOptions: [10, 20, 30, 40, 50],
          hasNext: (pageConfig.page || 1) < Math.ceil((totalCount ?? 0) / pageConfig.limit),
          hasPrevious: pageConfig.page > 1,
          total: Math.ceil((totalCount ?? 0) / pageConfig.limit),
          onNumRowsChange: (pageSize) => {
            setPageConfig((prev) => ({ ...prev, limit: pageSize, page: 1 }));
          },
          handleChange: (pageNumber: number) => {
            setPageConfig((prev) => ({
              ...prev,
              page: pageNumber,
            }));
          },
        }}
        styleConfig={{
          rows: (rowData) => {
            if (rowData.isArchived) {
              return css`
                color: ${theme.font.colors.disabled};
              `;
            }
            return;
          },
        }}
      />
    </>
  );
};
